import React, { forwardRef } from 'react';
import Text from '../Text';
import Box from '../Box';
import type { BoxProps } from '../Box/Box';

type BadgeVariant =
  | 'default'
  | 'primary'
  | 'negative'
  | 'past'
  | 'status/unassigned'
  | 'status/assigned'
  | 'status/committed'
  | 'status/shared'
  | 'status/estimate'
  | 'contractStatus/inquiry'
  | 'contractStatus/onSubs'
  | 'contractStatus/subsExpired'
  | 'portcalltimestatus/actual';

export interface BadgeProps extends BoxProps {
  isRounded?: boolean;
  children?: React.ReactNode;
  color?: string;
  variant?: BadgeVariant;
  fontSize?: number;
  onClick?: () => void;
}

const variants: {
  [key in BadgeVariant]: {
    bg: string;
    color: string;
    borderColor?: string;
    borderWidth?: string;
  };
} = {
  default: {
    bg: 'neutral.gray_175',
    color: 'text',
  },
  primary: {
    bg: 'yellow.yellow_300',
    color: 'neutral.black_900',
  },
  negative: {
    bg: 'status.error',
    color: 'neutral.white_50',
  },
  past: {
    bg: '#ffa500',
    color: 'text',
  },
  ['status/unassigned']: {
    bg: 'orderStatus.unassigned',
    color: 'neutral.white_50',
  },
  ['status/assigned']: {
    bg: 'orderStatus.assigned',
    color: 'neutral.white_50',
  },
  ['status/committed']: {
    bg: 'orderStatus.committed',
    color: 'neutral.white_50',
  },
  ['status/shared']: {
    bg: 'orderStatus.shared',
    color: 'neutral.white_50',
  },
  ['status/estimate']: {
    bg: 'orderStatus.estimate',
    color: 'neutral.white_50',
  },
  ['contractStatus/inquiry']: {
    bg: 'contractStatus.inquiry',
    color: 'neutral.black_700',
    borderColor: 'neutral.black_700',
    borderWidth: '2px',
  },
  ['contractStatus/onSubs']: {
    bg: 'contractStatus.onSubs',
    color: 'neutral.black_700',
    borderColor: 'neutral.black_700',
    borderWidth: '2px',
  },
  ['contractStatus/subsExpired']: {
    bg: 'contractStatus.subsExpired',
    color: 'neutral.black_700',
    borderColor: 'neutral.black_700',
    borderWidth: '2px',
  },
  ['portcalltimestatus/actual']: {
    bg: 'time.actual',
    color: '#fff',
  },
};

const clickableStyles = { cursor: 'pointer' };

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    { children, color, isRounded = false, variant = 'default', ...restProps },
    ref,
  ): JSX.Element => {
    const isClickable = restProps.onClick;

    return (
      <Box
        ref={ref}
        display="inline-flex"
        height={18}
        minWidth={18}
        px={2}
        alignItems="center"
        justifyContent="center"
        borderRadius={isRounded ? '99999px' : '0'}
        style={isClickable ? clickableStyles : undefined}
        {...variants[variant]}
        {...restProps}
      >
        <Text
          isUppercase
          isUnselectable
          fontWeight="bold"
          fontSize={0}
          isSingleLine
          color={color || variants[variant].color}
        >
          {children}
        </Text>
      </Box>
    );
  },
);

export default Badge;
